import { DownloadSimpleIcon } from "@phosphor-icons/react/dist/csr/DownloadSimple";
import type { ColumnDef } from "@tanstack/react-table";
import { useMemo, useState } from "react";
import { SimpleTable } from "@/core/components/simple-table";
import { Button } from "@volca/ui/components/ui/button";
import { EmptyTableMessage } from "@volca/ui/components/ui/empty-table-message";
import { Spinner } from "@volca/ui/components/ui/spinner";
import { Stack } from "@volca/ui/components/ui/stack";
import { SwitchWithLabel } from "@volca/ui/components/ui/switch-with-label";
import {
  TimeRangePicker,
  type TimeRange,
} from "@volca/ui/components/ui/time-range-picker";
import { cn } from "@volca/ui/lib/utils";
import { isProductEnabled } from "@/business/product-access";
import { USER_REPORTING_PRESETS } from "@/business-settings/common/user-reporting-presets";
import { useBusinessProducts } from "@/core/business/use-business-products";
import { useGetUserStatsByBusinessWithTimeRange } from "@/user/query";
import {
  ClosedReferralsCol,
  EmailCol,
  NameCol,
  ReferralCodeCol,
  RoleCol,
  SignupsCol,
  TotalReferralsCol,
  WorkbookAccessCol,
} from "./cols";
import type { AccountTableUser, WorkbookAccessColumnSettings } from "./types";
import { downloadUsersReport, getUsersReportData } from "./utils";

interface UsersTableProps {
  searchValue: string;
  onSearchChange: (value: string) => void;
  businessId?: string;
  data: AccountTableUser[];
  workbookAccess?: WorkbookAccessColumnSettings;
  isLoading?: boolean;
  onRowClick: (row: AccountTableUser) => void;
  selectedUserId?: string;
}

const baseColumns = [NameCol(), EmailCol(), RoleCol()];
const referralColumn = ReferralCodeCol();
const reportingColumns = [
  SignupsCol(),
  TotalReferralsCol(),
  ClosedReferralsCol(),
];

export const UsersTable = ({
  searchValue,
  onSearchChange,
  businessId,
  data,
  isLoading,
  onRowClick,
  selectedUserId,
  workbookAccess,
}: UsersTableProps) => {
  const { settings } = useBusinessProducts();
  const enableReferrals = isProductEnabled(settings, "referrals");
  const [showReporting, setShowReporting] = useState(false);
  const [timeRange, setTimeRange] = useState<TimeRange | null>(null);
  const reporting = enableReferrals && showReporting;
  const stats = useGetUserStatsByBusinessWithTimeRange(
    businessId,
    timeRange,
    reporting,
  );
  const tableData = useMemo(
    () => (reporting ? getUsersReportData(data, stats.data) : data),
    [data, stats.data, reporting],
  );
  const loading = isLoading || (reporting && stats.isLoading);

  const columns = useMemo(
    () =>
      [
        ...baseColumns,
        ...(workbookAccess ? [WorkbookAccessCol(workbookAccess)] : []),
        ...(enableReferrals ? [referralColumn] : []),
        ...(reporting ? reportingColumns : []),
      ] as ColumnDef<AccountTableUser>[],
    [enableReferrals, reporting, workbookAccess],
  );

  const handleDownload = () => {
    try {
      downloadUsersReport(tableData, timeRange);
    } catch (error) {
      console.error("Could not download users report", error);
    }
  };

  if (data.length === 0 && !loading)
    return <EmptyTableMessage>No users yet.</EmptyTableMessage>;

  return (
    <Stack gap={2} className="flex h-full min-h-0 w-full flex-col">
      {enableReferrals && (
        <Stack
          direction="row"
          justify="between"
          align="center"
          className="w-full shrink-0 flex-wrap gap-y-2 pr-5"
        >
          <SwitchWithLabel
            label="Show reporting"
            checked={reporting}
            onChange={setShowReporting}
          />
          {reporting && (
            <Stack direction="row" align="center" gap={1} className="flex-wrap">
              {stats.isLoading && (
                <Spinner size="sm" aria-label="Loading statistics" />
              )}
              <TimeRangePicker
                value={timeRange}
                onChange={setTimeRange}
                placeholder="Select date range"
                presets={USER_REPORTING_PRESETS}
              />
              <Button
                variant="ghost"
                size="sm"
                onClick={handleDownload}
                disabled={loading || stats.isLoading}
              >
                <DownloadSimpleIcon data-icon="inline-start" />
                Download report
              </Button>
            </Stack>
          )}
        </Stack>
      )}
      <SimpleTable
        label="Users"
        data={tableData}
        columns={columns}
        className="min-h-0 w-full flex-1 overflow-hidden"
        isLoading={loading}
        onRowClick={onRowClick}
        selectedRowId={selectedUserId}
        searchValue={searchValue}
        setSearchValue={onSearchChange}
        tableClassName={cn("min-w-full [&_tbody_tr]:h-14 [&_td]:py-2", {
          "mb-2 min-w-[1200px]": reporting,
        })}
      />
    </Stack>
  );
};
